import { DiscountDistributionMethod } from "@redotech/redo-return-core-common/exchange-settings";
import { ShopifyExchangeOrderStrategy } from "@redotech/redo-shopify-common/shopify";
import * as z from "zod";
import type { GiftCardRecipientEnum } from "./automation";
import {
  changedTeamPatchPaths,
  groupedSettingsChanges,
  sameSettingsValue,
} from "./grouped-settings-changes";
import {
  inventorySettingsFromTeam,
  inventorySettingsShape,
  inventoryTeamPatch,
} from "./operations/inventory-settings";
import {
  merchantCreatedReturnsSettingsFromTeam,
  merchantCreatedReturnsSettingsShape,
  merchantCreatedReturnsTeamPatch,
} from "./operations/merchant-created-returns-settings";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations/operations-settings-source";
import {
  ordersSettingsFromTeam,
  ordersSettingsShape,
  ordersTeamPatch,
} from "./operations/orders-settings";
import {
  pointOfSaleSettingsFromTeam,
  pointOfSaleSettingsShape,
  pointOfSaleTeamPatch,
} from "./operations/point-of-sale-settings";
import {
  processingEventsSettingsFromTeam,
  processingEventsSettingsShape,
  processingEventsToStored,
} from "./operations/processing-events-settings";
import {
  processingSettingsFromTeam,
  processingSettingsShape,
  processingTeamPatch,
} from "./operations/processing-settings";
import {
  returnPageDisplaySettingsFromTeam,
  returnPageDisplaySettingsShape,
  returnPageDisplayTeamPatch,
} from "./operations/return-page-display-settings";
import {
  shippingOptionsSettingsFromTeam,
  shippingOptionsSettingsShape,
  shippingOptionsTeamPatch,
} from "./operations/shipping-options-settings";
import {
  shopifySettingsFromTeam,
  shopifySettingsShape,
  shopifyTeamPatch,
} from "./operations/shopify-settings";
import {
  storeCreditSettingsFromTeam,
  storeCreditSettingsShape,
  storeCreditTeamPatch,
} from "./operations/store-credit-settings";
import type { ProcessingAutomation } from "./processing-automation";

export const ReturnsOperationsSettingsSchema = z.object({
  ...pointOfSaleSettingsShape,
  ...merchantCreatedReturnsSettingsShape,
  ...inventorySettingsShape,
  ...shopifySettingsShape,
  ...ordersSettingsShape,
  ...processingSettingsShape,
  ...processingEventsSettingsShape,
  ...shippingOptionsSettingsShape,
  ...storeCreditSettingsShape,
  ...returnPageDisplaySettingsShape,
});

export type ReturnsOperationsSettings = z.infer<
  typeof ReturnsOperationsSettingsSchema
>;

const settingsShape = ReturnsOperationsSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsOperationsSettingsChangesSchema = z.object({
  pointOfSale: settingsShape.pointOfSale.partial().optional(),
  merchantCreatedReturns: settingsShape.merchantCreatedReturns
    .partial()
    .optional(),
  staffPermissions: settingsShape.staffPermissions
    .unwrap()
    .partial()
    .optional(),
  inventory: settingsShape.inventory.partial().optional(),
  shopify: settingsShape.shopify.partial().optional(),
  storeCreditExpiration: settingsShape.storeCreditExpiration
    .partial()
    .optional(),
  general: settingsShape.general.partial().optional(),
  orders: settingsShape.orders.partial().optional(),
  orderNotes: settingsShape.orderNotes.partial().optional(),
  fulfillmentRegions: settingsShape.fulfillmentRegions.partial().optional(),
  fulfillment: settingsShape.fulfillment.partial().optional(),
  processing: settingsShape.processing.partial().optional(),
  returnProcessing: settingsShape.returnProcessing.partial().optional(),
  claimProcessing: settingsShape.claimProcessing.partial().optional(),
  greenReturns: settingsShape.greenReturns.partial().optional(),
  dropoffAutoProcessing: settingsShape.dropoffAutoProcessing
    .partial()
    .optional(),
  predefinedCategories: settingsShape.predefinedCategories.partial().optional(),
  agentReview: settingsShape.agentReview.partial().optional(),
  shipping: settingsShape.shipping.partial().optional(),
  storeCredit: settingsShape.storeCredit.partial().optional(),
  returnPageDisplay: settingsShape.returnPageDisplay.partial().optional(),
});

export type ReturnsOperationsSettingsChanges = z.infer<
  typeof ReturnsOperationsSettingsChangesSchema
>;

export function returnsOperationsSettingsChanges(
  initial: ReturnsOperationsSettings,
  next: ReturnsOperationsSettings,
): ReturnsOperationsSettingsChanges {
  return ReturnsOperationsSettingsChangesSchema.parse(
    groupedSettingsChanges(initial, next),
  );
}

/** Not re-validated: a legacy stored value that fails today's schema must not block saving an unrelated field. */
function applyReturnsOperationsSettingsChanges(
  base: ReturnsOperationsSettings,
  changes: ReturnsOperationsSettingsChanges,
): ReturnsOperationsSettings {
  return {
    pointOfSale: { ...base.pointOfSale, ...changes.pointOfSale },
    merchantCreatedReturns: {
      ...base.merchantCreatedReturns,
      ...changes.merchantCreatedReturns,
    },
    staffPermissions: base.staffPermissions && {
      ...base.staffPermissions,
      ...changes.staffPermissions,
    },
    inventory: { ...base.inventory, ...changes.inventory },
    shopify: { ...base.shopify, ...changes.shopify },
    storeCreditExpiration: {
      ...base.storeCreditExpiration,
      ...changes.storeCreditExpiration,
    },
    general: { ...base.general, ...changes.general },
    orders: { ...base.orders, ...changes.orders },
    orderNotes: { ...base.orderNotes, ...changes.orderNotes },
    fulfillmentRegions: {
      ...base.fulfillmentRegions,
      ...changes.fulfillmentRegions,
    },
    fulfillment: { ...base.fulfillment, ...changes.fulfillment },
    processing: { ...base.processing, ...changes.processing },
    returnProcessing: { ...base.returnProcessing, ...changes.returnProcessing },
    claimProcessing: { ...base.claimProcessing, ...changes.claimProcessing },
    greenReturns: { ...base.greenReturns, ...changes.greenReturns },
    dropoffAutoProcessing: {
      ...base.dropoffAutoProcessing,
      ...changes.dropoffAutoProcessing,
    },
    predefinedCategories: {
      ...base.predefinedCategories,
      ...changes.predefinedCategories,
    },
    agentReview: { ...base.agentReview, ...changes.agentReview },
    shipping: { ...base.shipping, ...changes.shipping },
    storeCredit: { ...base.storeCredit, ...changes.storeCredit },
    returnPageDisplay: {
      ...base.returnPageDisplay,
      ...changes.returnPageDisplay,
    },
  };
}

export function returnsOperationsSettingsFromTeam(
  source: ReturnsOperationsSettingsSource,
): ReturnsOperationsSettings {
  return {
    ...pointOfSaleSettingsFromTeam(source),
    ...merchantCreatedReturnsSettingsFromTeam(source),
    ...inventorySettingsFromTeam(source),
    ...shopifySettingsFromTeam(source),
    ...ordersSettingsFromTeam(source),
    ...processingSettingsFromTeam(source),
    ...processingEventsSettingsFromTeam(source),
    ...shippingOptionsSettingsFromTeam(source),
    ...storeCreditSettingsFromTeam(source),
    ...returnPageDisplaySettingsFromTeam(source),
  };
}

export interface ReturnsOperationsTeamPatch extends OperationsSettingsPatch {
  /** Absent when the Shopify integration should not be touched. */
  shopifyExchangeOrderStrategy?: ShopifyExchangeOrderStrategy;
  /** Top-level `team.automation.storeCreditExpiration`; absent when unchanged. */
  storeCreditExpiration?: { enabled: boolean; days: number };
  /** Top-level `team.automation.giftCardRecipient`; absent when unchanged. */
  giftCardRecipient?: GiftCardRecipientEnum;
  /** Top-level `team.automation` processing; absent when unchanged. Saving turns
   * `automation.enable` on and writes every method, as the old page did. */
  processingAutomation?: {
    enable: true;
    returnProcessing: ProcessingAutomation;
    claimProcessing: ProcessingAutomation;
  };
}

export function returnsOperationsSettingsToTeamPatch(
  current: ReturnsOperationsSettingsSource,
  next: ReturnsOperationsSettings,
): ReturnsOperationsTeamPatch {
  const { settings } = current;
  const groups = [
    pointOfSaleTeamPatch(next),
    merchantCreatedReturnsTeamPatch(next),
    inventoryTeamPatch(next),
    shopifyTeamPatch(next),
    ordersTeamPatch(current, next),
    processingTeamPatch(current, next),
    shippingOptionsTeamPatch(current, next),
    storeCreditTeamPatch(current, next),
    returnPageDisplayTeamPatch(next),
  ];
  const set: Record<string, unknown> = Object.assign(
    {},
    ...groups.map((group) => group.set),
  );
  const unset = groups.flatMap((group) => group.unset);

  const patch: ReturnsOperationsTeamPatch = {
    set,
    unset,
    storeCreditExpiration: {
      enabled: next.storeCreditExpiration.enabled,
      days: next.storeCreditExpiration.days,
    },
    giftCardRecipient: next.storeCredit.giftCardRecipient,
    processingAutomation: {
      enable: true,
      returnProcessing: processingEventsToStored(next.returnProcessing),
      claimProcessing: processingEventsToStored(next.claimProcessing),
    },
  };

  const turningShopifyReturnsOff =
    !!settings.createShopifyReturns && !next.shopify.createShopifyReturns;
  const requestedStrategy = turningShopifyReturnsOff
    ? ShopifyExchangeOrderStrategy.ORDER
    : next.orders.shopifyExchangeOrderStrategy;
  if (
    requestedStrategy !== null &&
    requestedStrategy !== current.shopifyExchangeOrderStrategy
  ) {
    patch.shopifyExchangeOrderStrategy = requestedStrategy;
    if (
      requestedStrategy === ShopifyExchangeOrderStrategy.RETURN &&
      settings.discountDistributionMethod === DiscountDistributionMethod.ORDER
    ) {
      set.discountDistributionMethod = DiscountDistributionMethod.LINE_ITEM;
    }
  }

  return patch;
}

/** Writes only what changed; untouched fields are rebuilt from storage so a stale form can't
 * revert other edits, and one change still emits every legacy field its encoding spans. */
export function returnsOperationsTeamPatchForChanges(
  current: ReturnsOperationsSettingsSource,
  changes: ReturnsOperationsSettingsChanges,
): ReturnsOperationsTeamPatch {
  const storedValue = returnsOperationsSettingsFromTeam(current);
  const baseline = returnsOperationsSettingsToTeamPatch(current, storedValue);
  const requested = returnsOperationsSettingsToTeamPatch(
    current,
    applyReturnsOperationsSettingsChanges(storedValue, changes),
  );

  const { set, unset } = changedTeamPatchPaths(baseline, requested);
  if (
    "exchanges.orderName.suffix" in set &&
    "exchanges.suffix" in requested.set
  ) {
    set["exchanges.suffix"] = null;
  }
  const patch: ReturnsOperationsTeamPatch = { set, unset };
  if (
    requested.shopifyExchangeOrderStrategy !== undefined &&
    requested.shopifyExchangeOrderStrategy !==
      baseline.shopifyExchangeOrderStrategy
  ) {
    patch.shopifyExchangeOrderStrategy = requested.shopifyExchangeOrderStrategy;
  }
  if (
    !sameSettingsValue(
      requested.storeCreditExpiration,
      baseline.storeCreditExpiration,
    )
  ) {
    patch.storeCreditExpiration = requested.storeCreditExpiration;
  }
  if (requested.giftCardRecipient !== baseline.giftCardRecipient) {
    patch.giftCardRecipient = requested.giftCardRecipient;
  }
  if (
    !sameSettingsValue(
      requested.processingAutomation,
      baseline.processingAutomation,
    )
  ) {
    patch.processingAutomation = requested.processingAutomation;
  }
  return patch;
}

/** The Shipping-page rows kept their shipping permission on the Operations tab. */
export function returnsOperationsChangesNeedShippingPermission(
  changes: ReturnsOperationsSettingsChanges,
): boolean {
  return Object.keys(changes.shipping ?? {}).length > 0;
}

/** Fee sync carries its own permission; the rest of the tab uses the returns one. */
export function returnsOperationsChangesNeedFeeSyncPermission(
  changes: ReturnsOperationsSettingsChanges,
): boolean {
  return changes.shopify?.syncReturnFeesToShopify !== undefined;
}
